/**
 * Score Components — HMVMReviewer Design System Phase 1
 * 
 * Display quality scores and ratings
 * 
 * Features:
 * - Score badge with color coding
 * - Score card with details
 * - Uses semantic theme tokens
 * - Square geometry (border-radius: 0)
 */

import { HTMLAttributes } from 'react';

export type ScoreLevel = 'excellent' | 'good' | 'fair' | 'poor';

export interface ScoreBadgeProps extends HTMLAttributes<HTMLSpanElement> {
  score: number;
  maxScore?: number;
  showLabel?: boolean;
}

function getScoreLevel(score: number, maxScore: number): ScoreLevel {
  const percentage = (score / maxScore) * 100;
  if (percentage >= 90) return 'excellent';
  if (percentage >= 70) return 'good';
  if (percentage >= 50) return 'fair';
  return 'poor';
}

const scoreLevelColors: Record<ScoreLevel, string> = {
  excellent: 'text-[var(--reviewer-score-excellent)] border-[var(--reviewer-success-border)] bg-[var(--reviewer-success-soft)]',
  good: 'text-[var(--reviewer-score-good)] border-[var(--reviewer-info-border)] bg-[var(--reviewer-info-soft)]',
  fair: 'text-[var(--reviewer-score-fair)] border-[var(--reviewer-warning-border)] bg-[var(--reviewer-warning-soft)]',
  poor: 'text-[var(--reviewer-score-poor)] border-[var(--reviewer-danger-border)] bg-[var(--reviewer-danger-soft)]',
};

const scoreLevelLabels: Record<ScoreLevel, string> = {
  excellent: 'Excellent',
  good: 'Good',
  fair: 'Fair',
  poor: 'Poor',
};

export function ScoreBadge({
  score,
  maxScore = 100,
  showLabel = false,
  className = '',
  ...props
}: ScoreBadgeProps) {
  const level = getScoreLevel(score, maxScore);
  
  return (
    <span
      className={`
        inline-flex items-center justify-center gap-2
        px-2 py-1
        border
        font-mono text-[var(--reviewer-text-sm)] font-medium
        ${scoreLevelColors[level]}
        ${className}
      `}
      {...props}
    >
      <span className="font-semibold">{score}/{maxScore}</span>
      {showLabel && (
        <>
          <span className="opacity-50">•</span>
          <span className="uppercase text-[var(--reviewer-text-xs)]">{scoreLevelLabels[level]}</span>
        </>
      )}
    </span>
  );
}

/**
 * Score Card - Detailed score display
 */
export interface ScoreCardProps extends HTMLAttributes<HTMLDivElement> {
  title: string;
  score: number;
  maxScore?: number;
  description?: string;
  details?: Array<{ label: string; value: string | number }>;
}

export function ScoreCard({
  title,
  score,
  maxScore = 100,
  description,
  details,
  className = '',
  ...props
}: ScoreCardProps) {
  const level = getScoreLevel(score, maxScore);
  const percentage = Math.round((score / maxScore) * 100);

  return (
    <div
      className={`
        border border-[var(--reviewer-border)]
        bg-[var(--reviewer-surface)]
        p-4
        ${className}
      `}
      {...props}
    >
      <div className="flex items-start justify-between gap-4 mb-3">
        <div>
          <h3 className="text-[var(--reviewer-text)] text-[var(--reviewer-text-lg)] font-medium font-mono m-0 mb-1">
            {title}
          </h3>
          {description && (
            <p className="text-[var(--reviewer-text-secondary)] text-[var(--reviewer-text-sm)] font-mono m-0">
              {description}
            </p>
          )}
        </div>
        <ScoreBadge score={score} maxScore={maxScore} showLabel />
      </div>
      
      {/* Progress Bar */}
      <div className="mb-3">
        <div className="h-2 bg-[var(--reviewer-bg-tertiary)] border border-[var(--reviewer-border-subtle)]">
          <div
            className={`h-full ${scoreLevelColors[level]}`}
            style={{ width: `${percentage}%` }}
          />
        </div>
      </div>
      
      {/* Details */}
      {details && details.length > 0 && (
        <div className="grid grid-cols-2 gap-2">
          {details.map((detail, index) => (
            <div key={index} className="flex justify-between items-center gap-2">
              <span className="text-[var(--reviewer-text-secondary)] text-[var(--reviewer-text-xs)] font-mono uppercase">
                {detail.label}
              </span>
              <span className="text-[var(--reviewer-text)] text-[var(--reviewer-text-sm)] font-mono font-medium">
                {detail.value}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
